import type { Product } from "@/lib/types";

const gbp = (amount: string) => ({ amount, currencyCode: "GBP" });

const img = (seed: string, w = 1200, h = 1200) => ({
  url: `https://picsum.photos/seed/tilefort-prod-${seed}/${w}/${h}`,
  altText: null,
  width: w,
  height: h,
});

type MockInput = {
  handle: string;
  title: string;
  vendor: string;
  productType: string;
  description: string;
  price: string;
  compareAt?: string;
  tags?: string[];
  sizes: string[];
  finishes?: string[];
  collections: string[];
};

function build({
  handle,
  title,
  vendor,
  productType,
  description,
  price,
  compareAt,
  tags = [],
  sizes,
  finishes,
  collections,
}: MockInput): Product {
  const baseImage = img(handle);
  const altImage = img(`${handle}-alt`);
  const detailImage = img(`${handle}-detail`);

  const options = [
    { id: `${handle}-opt-size`, name: "Size", values: sizes },
    ...(finishes
      ? [{ id: `${handle}-opt-finish`, name: "Finish", values: finishes }]
      : []),
  ];

  const variantCombos: { size: string; finish?: string }[] = [];
  for (const size of sizes) {
    if (finishes && finishes.length) {
      for (const finish of finishes) variantCombos.push({ size, finish });
    } else {
      variantCombos.push({ size });
    }
  }

  const variants = variantCombos.map((combo, idx) => ({
    id: `${handle}-v${idx + 1}`,
    title: combo.finish ? `${combo.size} / ${combo.finish}` : combo.size,
    availableForSale: idx % 7 !== 6,
    selectedOptions: [
      { name: "Size", value: combo.size },
      ...(combo.finish ? [{ name: "Finish", value: combo.finish }] : []),
    ],
    price: gbp(price),
    compareAtPrice: compareAt ? gbp(compareAt) : null,
    image: idx === 0 ? baseImage : null,
  }));

  return {
    id: `prod-${handle}`,
    handle,
    title,
    description,
    vendor,
    productType,
    tags,
    featuredImage: baseImage,
    images: [baseImage, altImage, detailImage],
    options,
    variants,
    priceRange: {
      minVariantPrice: gbp(price),
      maxVariantPrice: gbp(price),
    },
    compareAtPriceRange: compareAt
      ? {
          minVariantPrice: gbp(compareAt),
          maxVariantPrice: gbp(compareAt),
        }
      : undefined,
    availableForSale: true,
    collections,
  };
}

export const products: Product[] = [
  // Porcelain
  build({
    handle: "vienna-warm-oak-porcelain",
    title: "Vienna Warm Oak Porcelain",
    vendor: "Casa Nobile",
    productType: "Porcelain",
    description:
      "Sand-warm porcelain with a subtle linen texture. Ideal for kitchens, hallways and open-plan living.",
    price: "34.50",
    compareAt: "42.00",
    tags: ["bestseller", "kitchen"],
    sizes: ["600×600mm", "900×900mm", "1200×1200mm"],
    finishes: ["Matt", "Polished"],
    collections: ["porcelain"],
  }),
  build({
    handle: "alba-ivory-porcelain",
    title: "Alba Ivory Porcelain",
    vendor: "Casa Nobile",
    productType: "Porcelain",
    description:
      "A calm ivory porcelain with faint cloud movement. Brightens smaller bathrooms without feeling stark.",
    price: "32.00",
    sizes: ["600×600mm", "600×1200mm"],
    finishes: ["Matt", "Silk"],
    collections: ["porcelain"],
  }),
  build({
    handle: "marmo-statuario-porcelain",
    title: "Marmo Statuario Porcelain",
    vendor: "Atelier Milano",
    productType: "Porcelain",
    description:
      "Statuario marble-effect porcelain with soft grey veining. The look of Italian marble, built for real life.",
    price: "48.00",
    compareAt: "58.00",
    tags: ["new"],
    sizes: ["600×1200mm", "750×1500mm"],
    finishes: ["Polished", "Matt"],
    collections: ["porcelain", "marble"],
  }),
  build({
    handle: "luna-bone-porcelain",
    title: "Luna Bone Porcelain",
    vendor: "Nord Tile Co.",
    productType: "Porcelain",
    description:
      "Bone-soft porcelain with just enough grain to hide everyday use. A quiet floor you'll keep forever.",
    price: "29.50",
    sizes: ["300×600mm", "600×600mm"],
    finishes: ["Matt"],
    collections: ["porcelain"],
  }),
  build({
    handle: "noir-graphite-porcelain",
    title: "Noir Graphite Porcelain",
    vendor: "Nord Tile Co.",
    productType: "Porcelain",
    description:
      "Deep graphite porcelain with a velvet matt finish. Pairs boldly with pale grout or calmly with charcoal.",
    price: "36.00",
    tags: ["bestseller"],
    sizes: ["600×600mm", "900×900mm"],
    finishes: ["Matt"],
    collections: ["porcelain"],
  }),

  // Marble
  build({
    handle: "carrara-classic-marble",
    title: "Carrara Classic Marble",
    vendor: "Atelier Milano",
    productType: "Marble",
    description:
      "Honed Carrara with soft, wandering greys. A lifetime choice for bathrooms and feature walls.",
    price: "72.00",
    sizes: ["300×600mm", "600×600mm"],
    finishes: ["Honed", "Polished"],
    collections: ["marble"],
  }),
  build({
    handle: "calacatta-gold-marble",
    title: "Calacatta Gold Marble",
    vendor: "Atelier Milano",
    productType: "Marble",
    description:
      "Warm white Calacatta with gold and copper veining. Designed for signature walls and statement niches.",
    price: "96.00",
    compareAt: "112.00",
    tags: ["premium"],
    sizes: ["600×600mm", "600×1200mm"],
    finishes: ["Polished"],
    collections: ["marble"],
  }),
  build({
    handle: "emperador-brown-marble",
    title: "Emperador Brown Marble",
    vendor: "Pietra Nera",
    productType: "Marble",
    description:
      "Chocolate Emperador with latte veins. Adds depth to pale schemes and pairs beautifully with brass.",
    price: "68.00",
    sizes: ["300×600mm", "600×600mm"],
    finishes: ["Polished"],
    collections: ["marble"],
  }),
  build({
    handle: "travertine-silver-marble",
    title: "Travertine Silver Marble",
    vendor: "Pietra Nera",
    productType: "Marble",
    description:
      "Cool silver travertine with open-pore character. Soft underfoot, happy in wet rooms and steam.",
    price: "58.00",
    sizes: ["600×600mm", "600×1200mm"],
    finishes: ["Honed"],
    collections: ["marble"],
  }),
  build({
    handle: "onyx-misty-marble",
    title: "Onyx Misty Marble",
    vendor: "Pietra Nera",
    productType: "Marble",
    description:
      "Translucent onyx with misty movement. Best used on feature walls with backlighting for drama.",
    price: "128.00",
    tags: ["premium", "new"],
    sizes: ["600×1200mm"],
    finishes: ["Polished"],
    collections: ["marble"],
  }),

  // Metro
  build({
    handle: "classic-white-metro",
    title: "Classic White Metro",
    vendor: "Brick & Bond",
    productType: "Metro",
    description:
      "The original gloss white metro tile. Timeless, affordable, and installer-friendly.",
    price: "14.50",
    tags: ["bestseller"],
    sizes: ["75×150mm", "100×200mm"],
    finishes: ["Gloss", "Matt"],
    collections: ["metro"],
  }),
  build({
    handle: "sage-flat-metro",
    title: "Sage Flat Metro",
    vendor: "Brick & Bond",
    productType: "Metro",
    description:
      "Flat-edge metro in soft sage. The calm alternative to bevelled brights.",
    price: "18.00",
    sizes: ["75×150mm", "100×200mm"],
    finishes: ["Matt"],
    collections: ["metro"],
  }),
  build({
    handle: "terracotta-hand-metro",
    title: "Terracotta Hand-Glazed Metro",
    vendor: "Atelier Milano",
    productType: "Metro",
    description:
      "Hand-glazed metro in rich terracotta. Each tile varies slightly — that's the charm.",
    price: "28.00",
    compareAt: "34.00",
    tags: ["new"],
    sizes: ["75×150mm"],
    finishes: ["Gloss"],
    collections: ["metro", "terracotta"],
  }),
  build({
    handle: "charcoal-bevel-metro",
    title: "Charcoal Bevel Metro",
    vendor: "Brick & Bond",
    productType: "Metro",
    description:
      "Bevelled charcoal metro. A shadow-catching back-wall for splashbacks and showers.",
    price: "16.50",
    sizes: ["75×150mm", "100×200mm"],
    finishes: ["Gloss"],
    collections: ["metro"],
  }),
  build({
    handle: "powder-blue-metro",
    title: "Powder Blue Metro",
    vendor: "Brick & Bond",
    productType: "Metro",
    description:
      "Soft powder-blue gloss. Works with warm brass taps and pale oak surrounds.",
    price: "19.00",
    sizes: ["75×150mm"],
    finishes: ["Gloss"],
    collections: ["metro"],
  }),

  // Mosaic
  build({
    handle: "penny-round-bone-mosaic",
    title: "Penny Round Bone Mosaic",
    vendor: "Little Works",
    productType: "Mosaic",
    description:
      "Bone-white penny rounds on mesh. Shower floors, splashbacks and accent bands.",
    price: "12.00",
    sizes: ["305×305mm sheet"],
    collections: ["mosaic"],
  }),
  build({
    handle: "hex-slate-mosaic",
    title: "Hex Slate Mosaic",
    vendor: "Little Works",
    productType: "Mosaic",
    description:
      "Slate-grey hex mosaic — grippy, graphic, unbothered by soapy water.",
    price: "14.00",
    sizes: ["300×345mm sheet"],
    collections: ["mosaic"],
  }),
  build({
    handle: "fish-scale-teal-mosaic",
    title: "Fish-Scale Teal Mosaic",
    vendor: "Atelier Milano",
    productType: "Mosaic",
    description:
      "Glossy teal fish-scale. A scene-stealing feature wall for bathrooms that want to be photographed.",
    price: "22.00",
    tags: ["new"],
    sizes: ["275×305mm sheet"],
    collections: ["mosaic"],
  }),
  build({
    handle: "chevron-rose-mosaic",
    title: "Chevron Rose Mosaic",
    vendor: "Little Works",
    productType: "Mosaic",
    description:
      "Dusty-rose chevron mosaic. Warm, feminine, and stubbornly characterful.",
    price: "18.50",
    sizes: ["290×305mm sheet"],
    collections: ["mosaic"],
  }),
  build({
    handle: "square-ink-mosaic",
    title: "Square Ink Mosaic",
    vendor: "Brick & Bond",
    productType: "Mosaic",
    description:
      "Ink-black 20×20mm squares. Classic shower floor with grown-up restraint.",
    price: "11.00",
    sizes: ["300×300mm sheet"],
    collections: ["mosaic"],
  }),

  // Outdoor
  build({
    handle: "terrace-sandstone-outdoor",
    title: "Terrace Sandstone 20mm",
    vendor: "Outland Stone",
    productType: "Outdoor paver",
    description:
      "20mm porcelain sandstone paver. Frost-rated, slip-rated, ready for British weather.",
    price: "42.00",
    tags: ["bestseller"],
    sizes: ["600×900mm", "900×900mm"],
    finishes: ["Structured"],
    collections: ["outdoor"],
  }),
  build({
    handle: "garden-slate-outdoor",
    title: "Garden Slate 20mm",
    vendor: "Outland Stone",
    productType: "Outdoor paver",
    description:
      "Deep slate-black outdoor paver. Dramatic under wet-look planting.",
    price: "46.00",
    sizes: ["600×900mm"],
    finishes: ["Structured"],
    collections: ["outdoor"],
  }),
  build({
    handle: "patio-oak-outdoor",
    title: "Patio Oak 20mm",
    vendor: "Outland Stone",
    productType: "Outdoor paver",
    description:
      "Wood-effect 20mm paver in warm oak. Matches interior wood-effect for seamless thresholds.",
    price: "48.50",
    sizes: ["300×1200mm"],
    finishes: ["Structured"],
    collections: ["outdoor", "wood-effect"],
  }),
  build({
    handle: "court-concrete-outdoor",
    title: "Court Concrete 20mm",
    vendor: "Outland Stone",
    productType: "Outdoor paver",
    description:
      "Cool concrete-effect paver. Modernist courtyards and sharp drives.",
    price: "44.00",
    sizes: ["600×600mm", "600×1200mm"],
    finishes: ["Structured"],
    collections: ["outdoor"],
  }),
  build({
    handle: "path-limestone-outdoor",
    title: "Path Limestone 20mm",
    vendor: "Outland Stone",
    productType: "Outdoor paver",
    description:
      "Pale limestone paver with fossil-fleck. Garden paths and low-glare terraces.",
    price: "43.00",
    sizes: ["600×900mm"],
    finishes: ["Structured"],
    collections: ["outdoor"],
  }),

  // Terracotta
  build({
    handle: "tuscan-red-terracotta",
    title: "Tuscan Red Terracotta",
    vendor: "Casa Vecchia",
    productType: "Terracotta",
    description:
      "Hand-finished Tuscan terracotta. A warm, waxed floor for kitchens and utility rooms.",
    price: "52.00",
    tags: ["handmade"],
    sizes: ["200×200mm", "300×300mm"],
    finishes: ["Sealed"],
    collections: ["terracotta"],
  }),
  build({
    handle: "sienna-square-terracotta",
    title: "Sienna Square Terracotta",
    vendor: "Casa Vecchia",
    productType: "Terracotta",
    description:
      "Deep sienna square tile with hand-brushed edges. Mediterranean warmth without the upkeep.",
    price: "58.00",
    sizes: ["250×250mm"],
    finishes: ["Sealed"],
    collections: ["terracotta"],
  }),
  build({
    handle: "antique-rose-terracotta",
    title: "Antique Rose Terracotta",
    vendor: "Casa Vecchia",
    productType: "Terracotta",
    description:
      "Rose-blushed terracotta, kiln-variable. Works beautifully with limewashed walls.",
    price: "54.00",
    sizes: ["200×200mm"],
    finishes: ["Sealed"],
    collections: ["terracotta"],
  }),
  build({
    handle: "provence-sand-terracotta",
    title: "Provence Sand Terracotta",
    vendor: "Casa Vecchia",
    productType: "Terracotta",
    description:
      "Sand-pale terracotta. A softer entry point if full reds feel too forward.",
    price: "49.00",
    sizes: ["200×200mm", "300×300mm"],
    finishes: ["Sealed"],
    collections: ["terracotta"],
  }),
  build({
    handle: "umbria-ember-terracotta",
    title: "Umbria Ember Terracotta",
    vendor: "Casa Vecchia",
    productType: "Terracotta",
    description:
      "Ember-orange terracotta. Earthy, glowing, made for sunlit rooms and open fires.",
    price: "56.00",
    sizes: ["300×300mm"],
    finishes: ["Sealed"],
    collections: ["terracotta"],
  }),

  // Large format
  build({
    handle: "monolith-cloud-large-format",
    title: "Monolith Cloud 1200×2600mm",
    vendor: "Atelier Milano",
    productType: "Large format",
    description:
      "Calm, cloud-white slab. Fewer grout lines, calmer rooms.",
    price: "420.00",
    tags: ["premium"],
    sizes: ["1200×2600mm"],
    finishes: ["Polished", "Matt"],
    collections: ["large-format"],
  }),
  build({
    handle: "quartzite-iron-large-format",
    title: "Quartzite Iron 1200×2600mm",
    vendor: "Atelier Milano",
    productType: "Large format",
    description:
      "Deep iron slab with mineral streaks. Anchors kitchens and dressing rooms.",
    price: "480.00",
    sizes: ["1200×2600mm"],
    finishes: ["Polished"],
    collections: ["large-format"],
  }),
  build({
    handle: "blanco-nieve-large-format",
    title: "Blanco Nieve 1200×1200mm",
    vendor: "Casa Nobile",
    productType: "Large format",
    description:
      "Snow-white matte slab. The quiet hero of minimalist bathrooms.",
    price: "210.00",
    sizes: ["1200×1200mm", "1200×2400mm"],
    finishes: ["Matt"],
    collections: ["large-format"],
  }),
  build({
    handle: "slate-shadow-large-format",
    title: "Slate Shadow 1500×3000mm",
    vendor: "Atelier Milano",
    productType: "Large format",
    description:
      "Oversized slate-effect panel. Extraordinary for wet rooms and spas.",
    price: "560.00",
    tags: ["new"],
    sizes: ["1500×3000mm"],
    finishes: ["Matt"],
    collections: ["large-format"],
  }),
  build({
    handle: "concrete-pier-large-format",
    title: "Concrete Pier 1200×2400mm",
    vendor: "Nord Tile Co.",
    productType: "Large format",
    description:
      "Cement-grey slab with raw honesty. Loft-style kitchens and open showers.",
    price: "380.00",
    sizes: ["1200×2400mm"],
    finishes: ["Matt"],
    collections: ["large-format"],
  }),

  // Wood-effect
  build({
    handle: "oak-honey-plank",
    title: "Oak Honey Plank",
    vendor: "Nord Tile Co.",
    productType: "Wood-effect",
    description:
      "Honey-oak plank porcelain. The warmth of oak, the ease of tile.",
    price: "36.00",
    tags: ["bestseller"],
    sizes: ["200×1200mm"],
    finishes: ["Matt"],
    collections: ["wood-effect"],
  }),
  build({
    handle: "smoked-walnut-plank",
    title: "Smoked Walnut Plank",
    vendor: "Nord Tile Co.",
    productType: "Wood-effect",
    description:
      "Smoked walnut with tight grain. Grounds pale kitchens and spacious halls.",
    price: "38.00",
    sizes: ["200×1200mm"],
    finishes: ["Matt"],
    collections: ["wood-effect"],
  }),
  build({
    handle: "nordic-ash-plank",
    title: "Nordic Ash Plank",
    vendor: "Nord Tile Co.",
    productType: "Wood-effect",
    description:
      "Pale ash plank with silver undertones. Scandinavian calm, indoor-outdoor ready.",
    price: "34.50",
    sizes: ["200×1200mm", "225×1800mm"],
    finishes: ["Matt"],
    collections: ["wood-effect"],
  }),
  build({
    handle: "charred-pine-plank",
    title: "Charred Pine Plank",
    vendor: "Nord Tile Co.",
    productType: "Wood-effect",
    description:
      "Charred-pine plank, inspired by shou-sugi-ban. Dark, textural, unforgettable.",
    price: "39.50",
    sizes: ["200×1200mm"],
    finishes: ["Matt"],
    collections: ["wood-effect"],
  }),
  build({
    handle: "driftwood-pale-plank",
    title: "Driftwood Pale Plank",
    vendor: "Nord Tile Co.",
    productType: "Wood-effect",
    description:
      "Weather-washed driftwood tone. Coastal kitchens and bathrooms that want to feel bare-footed.",
    price: "36.50",
    sizes: ["200×1200mm"],
    finishes: ["Matt"],
    collections: ["wood-effect"],
  }),
];

export function findProduct(handle: string): Product | null {
  return products.find((p) => p.handle === handle) ?? null;
}

export function productsInCollection(collectionHandle: string): Product[] {
  return products.filter((p) => p.collections?.includes(collectionHandle));
}

export function featuredProducts(limit = 8): Product[] {
  return products.filter((p) => p.tags.includes("bestseller")).slice(0, limit);
}

export function newProducts(limit = 8): Product[] {
  return products.filter((p) => p.tags.includes("new")).slice(0, limit);
}
